import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import InvoiceDetailScreen from "./InvoiceDetailScreen.jsx";
import {
  INVOICE_STATUS_OPTIONS,
  arSummary,
  customersFromInvoices,
  daysPastDue,
  dsoLabel,
  invoiceRowInfo,
  invoiceStatusDisplay,
  loadAllInvoices,
  loadBookedRevenue,
  loadInvoices,
} from "../../features/invoices/invoiceData";
import { fmt } from "../../utils/financeFormat";
import { shopToday } from "../../utils/shopDate";
import { fmtDay } from "./salesOrders/format";
import "../mobile.css";

// The phone Invoices page (plan Phase 2, READ-ONLY): the list with the desktop's filters (search
// across every status, status, customer, dates) and the AR summary (outstanding, overdue, DSO,
// booked revenue, the overdue invoices); ?id=N opens one invoice (InvoiceDetailScreen). Nothing here
// writes: payments, edits, voids, Bill.com, emails, inquiries, deposits, credits, statements and
// sales tax stay on the desktop. Layout only: features/invoices/invoiceData.js is the desktop's code.

const CHIP = { success: "jq-status jq-status-done", info: "jq-status jq-status-progress", warning: "jq-tag jq-tag-warn", error: "jq-status jq-status-cancelled", default: "jq-tag" };

function invoiceId(searchParams) {
  const raw = searchParams.get("id");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

const LIST_START = { view: "list", searchInput: "", search: "", status: "Open", customerId: "", startDate: "", endDate: "", customers: [] };

export default function InvoicesScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listState, setListState] = useState(LIST_START);
  const navigate = useNavigate();
  const fromList = useRef(false);
  const id = invoiceId(searchParams);
  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else setSearchParams({}, { replace: true });
  };
  const open = (iid) => { fromList.current = true; setSearchParams({ id: String(iid) }); };
  if (id) return <InvoiceDetailScreen key={id} invoiceId={id} onAll={toList} />;
  return (
    <div className="jq-screen" data-testid="phone-invoices">
      <div className="d-flex gap-2 mb-2" role="group" aria-label="View">
        {[["list", "Invoices"], ["ar", "AR summary"]].map(([k, label]) => (
          <button key={k} type="button" className={`btn btn-sm ${listState.view === k ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={listState.view === k} onClick={() => setListState((p) => ({ ...p, view: k }))}>{label}</button>
        ))}
      </div>
      {listState.view === "ar" ? <ArSummary onOpen={open} /> : <InvoiceList state={listState} setState={setListState} onOpen={open} />}
    </div>
  );
}

function InvoiceCard({ inv, today }) {
  const { isPartiallyPaid, retainageOpen, retainage, totalPaid, invTotal } = invoiceRowInfo(inv);
  const { display, chipColor } = invoiceStatusDisplay(inv);
  const late = daysPastDue(inv, today);
  return (
    <div data-testid={`inv-card-${inv.invoice_id}`}>
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">#{inv.invoice_number}</span>
        <span className={CHIP[chipColor] || "jq-tag"}>{display}</span>
        {late > 0 && <span className="jq-tag jq-tag-block">{late}d past due</span>}
      </div>
      <div className="jq-qcard-title">{inv.customer_name || "N/A"}</div>
      <div className="jq-qcard-meta">
        {fmt(inv.total || 0)}
        {inv.due_date ? ` · due ${fmtDay(inv.due_date)}` : ""}
        {inv.order_number ? ` · ${inv.order_number}` : ""}
        {inv.customer_po ? ` · PO ${inv.customer_po}` : ""}
      </div>
      {isPartiallyPaid && !retainageOpen && <div className="jq-muted">Partial: {fmt(totalPaid)} of {fmt(invTotal)} paid</div>}
      {retainageOpen && <div className="jq-muted">{fmt(retainage)} held as retainage</div>}
      {(inv.last_emailed_at || inv.last_inquiry_at) && (
        <div className="jq-muted">
          {inv.last_emailed_at ? `Emailed ${new Date(inv.last_emailed_at).toLocaleDateString()}` : ""}
          {inv.last_emailed_at && inv.last_inquiry_at ? " · " : ""}
          {inv.last_inquiry_at ? `Inquiry sent ${new Date(inv.last_inquiry_at).toLocaleDateString()}` : ""}
        </div>
      )}
    </div>
  );
}

function InvoiceList({ state, setState, onOpen }) {
  // The customer list lives with the filters (state), so it is still there after an invoice and back.
  const { searchInput, search, status, customerId, startDate, endDate, customers } = state;
  const set = (key) => (value) => setState((prev) => ({ ...prev, [key]: value }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = shopToday();

  useEffect(() => {
    let live = true;
    setLoading(true);
    loadInvoices({ search, status, customerId, startDate, endDate })
      .then((data) => {
        if (!live) return;
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        // The desktop's customer filter lists the customers in the answer; with one picked, keep the list
        // it was picked from.
        if (!customerId) setState((prev) => ({ ...prev, customers: customersFromInvoices(list) }));
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setRows([]);
        setError(err?.response?.data?.detail || err.message || "Couldn't reach JobQ.");
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [search, status, customerId, startDate, endDate, setState]);

  const activeCount = (status !== "Open" ? 1 : 0) + (customerId ? 1 : 0) + (startDate ? 1 : 0) + (endDate ? 1 : 0);
  const label = search ? `Every invoice matching "${search}"` : null;

  return (
    <>
      {label && <div className="jq-muted mb-1">{label} (all statuses)</div>}
      <CardList
        items={rows}
        getKey={(r) => r.invoice_id}
        onItemClick={(r) => onOpen(r.invoice_id)}
        renderCard={(r) => <InvoiceCard inv={r} today={today} />}
        loading={loading}
        error={error}
        empty={search || activeCount ? "No invoices match." : "No open invoices."}
        testId="inv-list"
        search={{
          value: searchInput,
          onChange: (v) => setState((prev) => ({ ...prev, searchInput: v, search: v.trim() ? prev.search : "" })),
          onSubmit: (v) => set("search")(v.trim()),
          placeholder: "Invoice #, PO, SO#, job#, customer",
        }}
        filters={{
          activeCount,
          resultLabel: `${rows.length} invoice${rows.length === 1 ? "" : "s"}`,
          onClear: () => setState((prev) => ({ ...prev, status: "Open", customerId: "", startDate: "", endDate: "" })),
          render: () => (
            <>
              <div className="form-label">Status</div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {INVOICE_STATUS_OPTIONS.map((o) => (
                  <button key={o.value || "all"} type="button" className={`btn btn-sm ${status === o.value ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={status === o.value} onClick={() => set("status")(o.value)}>
                    {o.value ? o.label : "All"}
                  </button>
                ))}
              </div>
              <label className="form-label" htmlFor="inv-customer">Customer</label>
              <select id="inv-customer" className="form-select mb-3" value={customerId} onChange={(e) => set("customerId")(e.target.value)}>
                <option value="">All customers</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <label className="form-label" htmlFor="inv-from">Invoiced from</label>
                  <input id="inv-from" type="date" className="form-control" value={startDate} onChange={(e) => set("startDate")(e.target.value)} />
                </div>
                <div className="flex-grow-1">
                  <label className="form-label" htmlFor="inv-to">to</label>
                  <input id="inv-to" type="date" className="form-control" value={endDate} onChange={(e) => set("endDate")(e.target.value)} />
                </div>
              </div>
            </>
          ),
        }}
      />
    </>
  );
}

function ArSummary({ onOpen }) {
  const [summary, setSummary] = useState(null);
  const [booked, setBooked] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    loadAllInvoices()
      .then((all) => { if (live) setSummary(arSummary(all, shopToday()) || { empty: true }); })
      .catch((err) => { if (live) setError(err.message || "Couldn't reach JobQ."); });
    loadBookedRevenue()
      .then((b) => { if (live) setBooked(b); })
      .catch(() => { /* the AR numbers still show */ });
    return () => { live = false; };
  }, []);
  const overdue = useMemo(() => (summary?.overdueDetail || []).slice().sort((a, b) => b.days_overdue - a.days_overdue), [summary]);

  if (error) return <div className="jq-note jq-note-danger" role="alert">{error}</div>;
  if (!summary) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading every invoice…</div>;
  if (summary.empty) return <div className="jq-list-state">No invoices.</div>;
  return (
    <>
      <dl className="jq-kv" data-testid="ar-cards">
        <div><dt>AR outstanding</dt><dd>{fmt(summary.totalAR)}<div className="jq-muted">{summary.openCount} open</div></dd></div>
        <div><dt>Overdue</dt><dd className={summary.totalOverdue > 0 ? "text-danger" : ""}>{fmt(summary.totalOverdue)}<div className="jq-muted">{summary.overdueCount} past due</div></dd></div>
        <div><dt>Days sales outstanding</dt><dd>{summary.dso} days<div className="jq-muted">{dsoLabel(summary.dso)}</div></dd></div>
        <div>
          <dt>Booked, not invoiced</dt>
          <dd>
            {booked ? fmt(booked.total) : "—"}
            {booked && <div className="jq-muted">{booked.count} sales order{booked.count === 1 ? "" : "s"}{booked.tmPendingCount > 0 ? ` + ${booked.tmPendingCount} T&M not yet priced` : ""}</div>}
          </dd>
        </div>
      </dl>
      <h2 className="jq-section-title">Aging</h2>
      <dl className="jq-kv">
        {[["current", "Current"], ["d1_30", "1-30 days"], ["d31_60", "31-60 days"], ["d61_90", "61-90 days"], ["d90plus", "90+ days"]].map(([k, l]) => (
          <div key={k}><dt>{l}</dt><dd>{fmt(summary.agingBuckets[k])}</dd></div>
        ))}
      </dl>
      <h2 className="jq-section-title">Overdue invoices</h2>
      {overdue.length === 0 && <div className="jq-list-state">Nothing past due.</div>}
      {overdue.map((o) => (
        <button key={o.invoice_id} type="button" className="jq-card jq-card-button" onClick={() => onOpen(o.invoice_id)} data-testid={`ar-overdue-${o.invoice_id}`}>
          <div className="jq-qcard-top">
            <span className="jq-qcard-job">#{o.invoice_number}</span>
            <span className={o.days_overdue > 60 ? "jq-tag jq-tag-block" : "jq-tag jq-tag-warn"}>{o.days_overdue}d</span>
          </div>
          <div className="jq-qcard-title">{o.customer_name}</div>
          <div className="jq-qcard-meta">
            {fmt(o.balance)} due {fmtDay(o.due_date)}
            {o.last_inquiry_at ? ` · inquiry ${new Date(o.last_inquiry_at).toLocaleDateString()}` : o.last_emailed_at ? ` · emailed ${new Date(o.last_emailed_at).toLocaleDateString()}` : " · never emailed"}
          </div>
          <i className="fas fa-chevron-right jq-card-chevron" aria-hidden="true" />
        </button>
      ))}
    </>
  );
}
